import React from "react";
import FeatureCard from "./FeatureCard";
import cardData from "./data";
import { Type, Anchor } from "discover.medical.react-component-library";
import { Link } from "react-router-dom";
import { useConfig } from "@eis-poc/lib-discover-medical-mfes";
import useCTA, { CTA_TRIGGER_ACTION } from "../../util/useCTA";
import { useEventBus, EVENT_BUS_EVENTS } from "../../util/useEventBus";
import { StyledDescriptionContent } from "./_.styled";
import { logResourceTileLinkClicked } from "../../util/analytics";
import Epic from "../../util/epic";

const FeatureCardContent = ({ contents }) => {
  const eventBus = useEventBus();
  const { hasCTA } = useCTA();

  const handleClick = (e, isGated, telemetryAffordance, telemetryCallback) => {
    logResourceTileLinkClicked(telemetryAffordance);
    telemetryCallback?.();
    if (isGated) {
      hasCTA(e, CTA_TRIGGER_ACTION.FEATURE_CARD_CONTENT_CLICK);
    }
  };

  const handleEventBusAction = (actionDefinition, e) => {
    const {
      eventBusActionName,
      eventBusActionPayload,
      telemetryAffordance,
    } = actionDefinition;
    logResourceTileLinkClicked(telemetryAffordance);

    if (hasCTA(e, CTA_TRIGGER_ACTION.FEATURE_CARD_CONTENT_CLICK)) {
      return;
    }

    const requestId = Math.floor(Math.random() * 1000000).toString(16);
    eventBusActionPayload.requestId = requestId;

    const successHandler = (context) => {
      const {
        requestId: responseId,
        response: { urlWithToken },
      } = context;

      if (responseId === requestId) {
        eventBus.detach(EVENT_BUS_EVENTS.RECEIVE_TOKENIZED_URL);
        const epic = new Epic();
        if (epic.isEpic()) {
          epic.openExternalWindow(urlWithToken);
        } else {
          window.open(urlWithToken, "_blank");
        }
      }
    };
    eventBus.on(EVENT_BUS_EVENTS.RECEIVE_TOKENIZED_URL, successHandler);

    eventBus.emit(eventBusActionName, null, eventBusActionPayload);
  };

  return (
    <>
      {contents?.map((content, index) => (
        <React.Fragment key={index}>
          {content.descriptionWithEmbeddedAction && (
            <Type element="p" fontSize="sm" marginBottom="sm">
              {content.descriptionWithEmbeddedAction.beginning}
              <Anchor
                href={
                  content.descriptionWithEmbeddedAction.action
                    .eventBusActionPayload.url
                }
                data-testid="eventBus-trigger"
                fontSize="sm"
                onClick={(e) => {
                  e.preventDefault();
                  if (e.button !== 1) {
                    handleEventBusAction(
                      content.descriptionWithEmbeddedAction.action,
                      e
                    );
                  }
                }}
                onContextMenu={(e) => e.preventDefault()}
                onAuxClick={(e) => e.preventDefault()}
              >
                <StyledDescriptionContent
                  dangerouslySetInnerHTML={{
                    __html: content.descriptionWithEmbeddedAction.action.text,
                  }}
                />
              </Anchor>
              {content.descriptionWithEmbeddedAction.end}
            </Type>
          )}
          {content.action?.type === "link" && (
            <Anchor
              fontWeight="bold"
              linkComponent={content.action.linkComponent || Link}
              to={content.action.route}
              onClick={(e) =>
                handleClick(
                  e,
                  content.action.gated,
                  content.action.telemetryAffordance,
                  content.action.telemetryCallback
                )
              }
              href={content.action.route}
              data-gated={content.action.gated ? "gated" : "open"}
            >
              {content.action.text}
            </Anchor>
          )}
          {content.description && (
            <>
              <Type element="p" fontSize="sm" marginBottom="sm">
                <StyledDescriptionContent
                  dangerouslySetInnerHTML={{ __html: content.description }}
                />
              </Type>
            </>
          )}
        </React.Fragment>
      ))}
    </>
  );
};

const FeatureCards = ({
  data = cardData,
  defaultProductCode = "dmx",
  className,
}) => {
  const productCode =
    useConfig()?.productInfo?.productCode || defaultProductCode;

  return (
    <section className={className}>
      {data[productCode].map((card, index) => {
        return (
          <FeatureCard
            key={index}
            heading={card.heading}
            buttonText={card.action?.text}
            route={card.action?.route}
            landscapeSrc={card.imageSrc?.landscape}
            portraitSrc={card.imageSrc?.portrait}
            iconSrc={card.imageSrc?.icon}
            isGated={card.action?.gated}
          >
            <FeatureCardContent contents={card.contents} />
          </FeatureCard>
        );
      })}
    </section>
  );
};

export default FeatureCards;
